import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import PageLayout from '../components/common/PageLayout';
import { get } from '../utils/api';
import AnalyticsCompareView from './AnalyticsComparePage';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import PrintIcon from '@mui/icons-material/Print';
import { getKotilingaCategorySlugsCsv } from '../utils/kotilingaScope';

const PRESETS = [
  { k: 'last_12_months', t: 'Last 12 months' },
  { k: 'this_month', t: 'This month' },
  { k: 'last_3_months', t: 'Last 3 months' },
  { k: 'this_year', t: 'This year' },
  { k: 'last_year', t: 'Last year' },
  { k: 'all', t: 'All time' },
  { k: 'custom', t: 'Custom' },
];

function toYMD(d) {
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

function formatCurrency(n) {
  const num = parseFloat(n);
  if (isNaN(num)) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(num);
}

function formatDateLabel(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', day: d.getUTCHours() || d.getHours() ? 'numeric' : undefined });
}

function formatLabelByInterval(iso, interval) {
  if (!iso) return '';
  const d = new Date(iso);
  if (interval === 'year') return String(d.getUTCFullYear());
  if (interval === 'month') return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  if (interval === 'week') return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Distinct color families (blue, green, red, orange, purple, teal, amber, deep purple)
const CHART_COLORS = ['#1565c0', '#2e7d32', '#c62828', '#ef6c00', '#6a1b9a', '#00838f', '#f9a825', '#4a148c', '#00695c', '#ad1457'];

function AnalyticsTrendsView() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const viewParam = searchParams.get('view');
  const initialViewMode =
    viewParam === 'kotilinga' || viewParam === 'all' ? viewParam : 'temple';
  const [rangePreset, setRangePreset] = useState('last_12_months');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateRangeBounds, setDateRangeBounds] = useState({ minDate: null, maxDate: null });
  const [viewMode, setViewMode] = useState(initialViewMode); // 'temple' | 'kotilinga' | 'all'
  const [summary, setSummary] = useState(null);
  const [revenueSeries, setRevenueSeries] = useState([]);
  const [orderCountSeries, setOrderCountSeries] = useState([]);
  const [productSplit, setProductSplit] = useState([]);
  const [categorySplit, setCategorySplit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [chartInterval, setChartInterval] = useState('month'); // day | week | month | year
  const printRef = useRef(null);
  const ROWS_PER_PAGE = 10;
  const [productPage, setProductPage] = useState(0);
  const [categoryPage, setCategoryPage] = useState(0);
  const [productOptions, setProductOptions] = useState([]);
  const [variationOptions, setVariationOptions] = useState([]);
  const [productFilterId, setProductFilterId] = useState('');
  const [variationFilterId, setVariationFilterId] = useState('');
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoryFilterId, setCategoryFilterId] = useState('');

  const applyPreset = useCallback((preset) => {
    if (preset === 'custom') return { startIso: '', endIso: '' };
    const now = new Date();
    const end = new Date(now);
    const start = new Date(now);
    let startYmd = '';
    let endYmd = toYMD(end);
    switch (preset) {
      case 'this_month':
        start.setDate(1);
        startYmd = toYMD(start);
        break;
      case 'last_3_months':
        start.setMonth(now.getMonth() - 2);
        startYmd = toYMD(start);
        break;
      case 'this_year':
        start.setMonth(0, 1);
        startYmd = toYMD(start);
        break;
      case 'last_year': {
        start.setFullYear(now.getFullYear() - 1);
        start.setMonth(0, 1);
        const endLastYear = new Date(now.getFullYear() - 1, 11, 31);
        startYmd = toYMD(start);
        endYmd = toYMD(endLastYear);
        break;
      }
      case 'all':
        startYmd = '';
        endYmd = '';
        break;
      default:
        // last_12_months
        start.setFullYear(now.getFullYear() - 1);
        startYmd = toYMD(start);
        break;
    }
    const startIso = startYmd ? new Date(start.getFullYear(), start.getMonth(), start.getDate()).toISOString() : '';
    let endIso = '';
    if (endYmd) {
      const endD = new Date(endYmd);
      endD.setHours(23, 59, 59, 999);
      endIso = endD.toISOString();
    }
    return { startIso, endIso };
  }, []);

  const loadDateRange = useCallback(async () => {
    try {
      const r = await get('/ldb/analytics/date-range');
      setDateRangeBounds({ minDate: r?.minDate || null, maxDate: r?.maxDate || null });
    } catch (e) {
      console.warn('Analytics date-range failed', e?.message || e);
    }
  }, []);

  const loadProductOptions = useCallback(async () => {
    try {
      const params = { limit: 400 };
      const scopeSlugs = getKotilingaCategorySlugsCsv();
      if (viewMode === 'temple') params.exclude_parent_category_slugs = scopeSlugs;
      else if (viewMode === 'kotilinga') params.parent_category_slugs = scopeSlugs;
      if (categoryFilterId) params.filter_category_id = categoryFilterId;
      const r = await get('/ldb/facets/products', params);
      setProductOptions(Array.isArray(r?.products) ? r.products : []);
    } catch (e) {
      console.warn('Analytics product list failed', e?.message || e);
      setProductOptions([]);
    }
  }, [viewMode, categoryFilterId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await get('/ldb/categories');
        const cats = Array.isArray(r?.categories) ? r.categories : [];
        if (!cancelled) setCategoryOptions([...cats].sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), undefined, { sensitivity: 'base' })));
      } catch (e) {
        if (!cancelled) console.warn('Analytics categories list failed', e?.message || e);
        if (!cancelled) setCategoryOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    loadProductOptions();
  }, [loadProductOptions]);

  useEffect(() => {
    setProductFilterId('');
    setVariationFilterId('');
    setCategoryFilterId('');
  }, [viewMode]);

  useEffect(() => {
    setProductFilterId('');
    setVariationFilterId('');
  }, [categoryFilterId]);

  useEffect(() => {
    if (!productFilterId) {
      setVariationOptions([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await get(`/ldb/facets/variations?product_id=${encodeURIComponent(productFilterId)}&limit=300`);
        if (!cancelled) setVariationOptions(Array.isArray(r?.variations) ? r.variations : []);
      } catch (e) {
        if (!cancelled) {
          console.warn('Analytics variations failed', e?.message || e);
          setVariationOptions([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [productFilterId]);

  const loadData = useCallback(async (startIso, endIso, view) => {
    setLoading(true);
    setError('');
    const effectiveView = view ?? viewMode;
    try {
      const params = {};
      if (startIso) params.start = startIso;
      if (endIso) params.end = endIso;
      const scopeSlugsCsv = getKotilingaCategorySlugsCsv();
      if (effectiveView === 'temple') params.exclude_parent_category_slugs = scopeSlugsCsv;
      else if (effectiveView === 'kotilinga') params.parent_category_slugs = scopeSlugsCsv;
      if (categoryFilterId) params.filter_category_id = categoryFilterId;
      if (productFilterId) params.product_id = productFilterId;
      if (variationFilterId) params.variation_id = variationFilterId;

      let interval = 'month';
      if (!startIso && !endIso) interval = 'year';
      else if (startIso && endIso) {
        const startMs = new Date(startIso).getTime();
        const endMs = new Date(endIso).getTime();
        const days = (endMs - startMs) / (24 * 60 * 60 * 1000);
        if (days <= 31) interval = 'day';
        else if (days <= 95) interval = 'week';
        else if (days <= 400) interval = 'month';
        else interval = 'year';
      }
      setChartInterval(interval);

      const [sumRes, revRes, ordRes, prodRes, catRes] = await Promise.all([
        get('/ldb/analytics/summary', params),
        get('/ldb/analytics/revenue-series', { ...params, interval }),
        get('/ldb/analytics/order-count-series', { ...params, interval }),
        get('/ldb/analytics/product-split', { ...params, limit: 100 }),
        get('/ldb/analytics/category-split', { ...params, limit: 100 }),
      ]);
      setSummary(sumRes?.summary || null);
      setRevenueSeries(Array.isArray(revRes?.series) ? revRes.series : []);
      setOrderCountSeries(Array.isArray(ordRes?.series) ? ordRes.series : []);
      setProductSplit(Array.isArray(prodRes?.data) ? prodRes.data : []);
      setCategorySplit(Array.isArray(catRes?.data) ? catRes.data : []);
      setProductPage(0);
      setCategoryPage(0);
    } catch (e) {
      setError(e?.message || 'Failed to load analytics');
      setSummary(null);
      setRevenueSeries([]);
      setOrderCountSeries([]);
      setProductSplit([]);
      setCategorySplit([]);
      setProductPage(0);
      setCategoryPage(0);
    } finally {
      setLoading(false);
    }
  }, [viewMode, productFilterId, variationFilterId, categoryFilterId]);

  useEffect(() => {
    loadDateRange();
  }, [loadDateRange]);

  useEffect(() => {
    if (rangePreset === 'custom') {
      if (startDate && endDate) {
        const startIso = new Date(startDate).toISOString();
        const endD = new Date(endDate);
        endD.setHours(23, 59, 59, 999);
        loadData(startIso, endD.toISOString());
      } else {
        setSummary(null);
        setRevenueSeries([]);
        setOrderCountSeries([]);
        setProductSplit([]);
        setCategorySplit([]);
        setLoading(false);
      }
      return;
    }
    const { startIso, endIso } = applyPreset(rangePreset);
    if (rangePreset === 'all') {
      loadData('', '');
    } else {
      loadData(startIso, endIso);
    }
  }, [rangePreset, startDate, endDate, viewMode, productFilterId, variationFilterId, categoryFilterId, applyPreset, loadData]);

  const periodLabel = useMemo(() => {
    if (rangePreset === 'custom' && startDate && endDate) return `${startDate} to ${endDate}`;
    const p = PRESETS.find((x) => x.k === rangePreset);
    return p ? p.t : rangePreset;
  }, [rangePreset, startDate, endDate]);

  const productVariationExportLabel = useMemo(() => {
    if (!productFilterId) return '';
    const p = productOptions.find((x) => String(x.id) === String(productFilterId));
    const pname = (p?.name || `Product ${productFilterId}`).slice(0, 80);
    if (!variationFilterId) return `Product: ${pname} (all variations)`;
    const v = variationOptions.find((x) => String(x.id) === String(variationFilterId));
    const vlabel = (v?.display_name || v?.sku || `Variation ${variationFilterId}`).slice(0, 80);
    return `Product: ${pname} | Variation: ${vlabel}`;
  }, [productFilterId, variationFilterId, productOptions, variationOptions]);

  const revenueChartData = useMemo(() =>
    revenueSeries.map((s) => ({
      ...s,
      label: formatLabelByInterval(s.bucket, chartInterval),
      amountNum: parseFloat(s.amount) || 0,
    })), [revenueSeries, chartInterval]);

  const orderChartData = useMemo(() =>
    orderCountSeries.map((s) => ({
      ...s,
      label: formatLabelByInterval(s.bucket, chartInterval),
      ordersNum: parseInt(s.orders, 10) || 0,
    })), [orderCountSeries, chartInterval]);

  const PIE_TOP_N = 15;
  const OTHERS_COLOR = '#9e9e9e';

  const pieData = useMemo(() => {
    if (!productSplit.length) return [];
    const top = productSplit.slice(0, PIE_TOP_N).map((r, i) => ({
      name: (r.product_name || `Product ${r.product_id}`).slice(0, 30),
      value: parseFloat(r.amount) || 0,
      color: CHART_COLORS[i % CHART_COLORS.length],
    }));
    if (productSplit.length <= PIE_TOP_N) return top;
    const othersSum = productSplit.slice(PIE_TOP_N).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
    if (othersSum <= 0) return top;
    return [...top, { name: 'Others', value: othersSum, color: OTHERS_COLOR }];
  }, [productSplit]);

  const categoryPieData = useMemo(() => {
    if (!categorySplit.length) return [];
    const top = categorySplit.slice(0, PIE_TOP_N).map((r, i) => ({
      name: (r.category_name || `Category ${r.category_id}`).slice(0, 30),
      value: parseFloat(r.amount) || 0,
      color: CHART_COLORS[i % CHART_COLORS.length],
    }));
    if (categorySplit.length <= PIE_TOP_N) return top;
    const othersSum = categorySplit.slice(PIE_TOP_N).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
    if (othersSum <= 0) return top;
    return [...top, { name: 'Others', value: othersSum, color: OTHERS_COLOR }];
  }, [categorySplit]);

  const handleDownloadCSV = () => {
    const lines = [];
    if (productVariationExportLabel) lines.push(productVariationExportLabel);
    lines.push('Revenue over time');
    lines.push('Period,Amount');
    revenueChartData.forEach((r) => lines.push(`${r.label},${r.amountNum}`));
    lines.push('');
    lines.push('Order count over time');
    lines.push('Period,Orders');
    orderChartData.forEach((r) => lines.push(`${r.label},${r.ordersNum}`));
    lines.push('');
    lines.push('Revenue by product');
    lines.push('Product,Amount,Orders');
    productSplit.forEach((r) => lines.push(`"${(r.product_name || '').replace(/"/g, '""')}",${parseFloat(r.amount) || 0},${r.orders || 0}`));
    lines.push('');
    lines.push('Revenue by category');
    lines.push('Category,Amount,Orders');
    categorySplit.forEach((r) => lines.push(`"${(r.category_name || '').replace(/"/g, '""')}",${parseFloat(r.amount) || 0},${r.orders || 0}`));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `analytics_${periodLabel.replace(/\s/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const title = 'Analytics & Trends';
    doc.setFontSize(16);
    doc.text(title, 14, 22);
    doc.setFontSize(10);
    doc.text(`Period: ${periodLabel} | Generated: ${new Date().toLocaleString()}`, 14, 36);
    let y = 48;
    if (productVariationExportLabel) {
      y += 14;
      doc.text(productVariationExportLabel, 14, y);
    }
    y += 16;
    if (summary) {
      doc.setFontSize(11);
      doc.text(`Total revenue: ${formatCurrency(summary.total_revenue)}`, 14, y);
      y += 16;
      doc.text(`Order count: ${summary.order_count || 0}`, 14, y);
      y += 16;
      doc.text(`Avg order value: ${formatCurrency(summary.avg_order_value)}`, 14, y);
      y += 24;
    }
    const tableData = productSplit.map((r) => [
      (r.product_name || `Product ${r.product_id}`).slice(0, 40),
      formatCurrency(r.amount),
      String(r.orders || 0),
    ]);
    if (tableData.length > 0) {
      doc.setFontSize(11);
      doc.text('Revenue by product', 14, y);
      y += 8;
      autoTable(doc, {
        head: [['Product', 'Amount', 'Orders']],
        body: tableData,
        startY: y,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [26, 35, 126] },
      });
      y = doc.lastAutoTable ? doc.lastAutoTable.finalY + 14 : y + 14;
    }
    const catTableData = categorySplit.map((r) => [
      (r.category_name || `Category ${r.category_id}`).slice(0, 40),
      formatCurrency(r.amount),
      String(r.orders || 0),
    ]);
    if (catTableData.length > 0) {
      doc.setFontSize(11);
      doc.text('Revenue by category', 14, y);
      y += 8;
      autoTable(doc, {
        head: [['Category', 'Amount', 'Orders']],
        body: catTableData,
        startY: y,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [26, 35, 126] },
      });
    }
    doc.save(`Analytics_${periodLabel.replace(/\s/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const handlePrint = () => {
    if (!printRef.current) return;
    const content = printRef.current.innerHTML;
    const win = window.open('', '_blank');
    win.document.write(`
      <!DOCTYPE html><html><head><title>Analytics & Trends</title>
      <style>
        body { font-family: Arial,sans-serif; padding: 16px; }
        .section { margin-bottom: 24px; }
        table { border-collapse: collapse; width: 100%; font-size: 12px; }
        th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
        th { background: #1a237e; color: white; }
      </style></head><body><h1>Analytics & Trends</h1><p>Period: ${periodLabel} | Generated: ${new Date().toLocaleString()}</p>${productVariationExportLabel ? `<p>${productVariationExportLabel.replace(/</g, '&lt;')}</p>` : ''}${content}</body></html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => {
      win.print();
      win.close();
    }, 300);
  };

  return (
    <>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" flexWrap="wrap" justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Date range</InputLabel>
              <Select
                label="Date range"
                value={rangePreset}
                onChange={(e) => {
                  const v = e.target.value;
                  setRangePreset(v);
                  if (v === 'custom') {
                    const min = dateRangeBounds.minDate ? toYMD(new Date(dateRangeBounds.minDate)) : '';
                    const max = dateRangeBounds.maxDate ? toYMD(new Date(dateRangeBounds.maxDate)) : toYMD(new Date());
                    setStartDate(min);
                    setEndDate(max);
                  }
                }}
              >
                {PRESETS.map((opt) => (
                  <MenuItem key={opt.k} value={opt.k}>
                    {opt.t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {rangePreset === 'custom' && (
              <>
                <TextField type="date" label="Start" size="small" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField type="date" label="End" size="small" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
              </>
            )}
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Category</InputLabel>
              <Select
                label="Category"
                value={categoryFilterId || ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setCategoryFilterId(v ? String(v) : '');
                }}
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {categoryOptions.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {(c.name || `Category ${c.id}`).slice(0, 70)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Product</InputLabel>
              <Select
                label="Product"
                value={productFilterId || ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setProductFilterId(v ? String(v) : '');
                  setVariationFilterId('');
                }}
              >
                <MenuItem value="">
                  <em>All products</em>
                </MenuItem>
                {productOptions.map((p) => (
                  <MenuItem key={p.id} value={String(p.id)}>
                    {(p.name || `Product ${p.id}`).slice(0, 70)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }} disabled={!productFilterId}>
              <InputLabel>Variation</InputLabel>
              <Select
                label="Variation"
                value={variationFilterId || ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setVariationFilterId(v ? String(v) : '');
                }}
              >
                <MenuItem value="">
                  <em>All variations</em>
                </MenuItem>
                {variationOptions.map((v) => (
                  <MenuItem key={v.id} value={String(v.id)}>
                    {(v.display_name || v.sku || `Variation ${v.id}`).slice(0, 70)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Button variant="outlined" size="small" startIcon={<TableChartIcon />} onClick={handleDownloadCSV}>
              CSV
            </Button>
            <Button variant="outlined" size="small" startIcon={<PictureAsPdfIcon />} onClick={handleDownloadPDF}>
              PDF
            </Button>
            <Button variant="outlined" size="small" startIcon={<PrintIcon />} onClick={handlePrint}>
              Print
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
      )}

      {loading ? (
        <Typography color="text.secondary">Loading…</Typography>
      ) : (
        <Box ref={printRef}>
          {summary && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="text.secondary" variant="body2">Total revenue</Typography>
                    <Typography variant="h5">{formatCurrency(summary.total_revenue)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="text.secondary" variant="body2">Order count</Typography>
                    <Typography variant="h5">{summary.order_count ?? 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="text.secondary" variant="body2">Avg order value</Typography>
                    <Typography variant="h5">{formatCurrency(summary.avg_order_value)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {revenueChartData.length > 0 && (
            <Paper sx={{ p: 2, mb: 2 }} className="section">
              <Typography variant="h6" gutterBottom>Revenue over time</Typography>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={revenueChartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={revenueChartData.length > 12 ? Math.max(0, Math.floor((revenueChartData.length - 1) / 10)) : 0} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                  <Tooltip formatter={(v) => [formatCurrency(v), 'Revenue']} labelFormatter={(l) => l} />
                  <Line type="monotone" dataKey="amountNum" stroke="#1a237e" strokeWidth={2} dot={{ r: 3 }} name="Revenue" />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          )}

          {orderChartData.length > 0 && (
            <Paper sx={{ p: 2, mb: 2 }} className="section">
              <Typography variant="h6" gutterBottom>Order count over time</Typography>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={orderChartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={orderChartData.length > 12 ? Math.max(0, Math.floor((orderChartData.length - 1) / 10)) : 0} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="ordersNum" fill="#2e7d32" name="Orders" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          )}

          <Paper sx={{ p: 2, mb: 2, width: '100%', boxSizing: 'border-box' }} className="section">
            <Typography variant="h6" gutterBottom>Revenue by product</Typography>
            <Box sx={{ display: 'flex', width: '100%', gap: 2, flexDirection: 'column' }}>
              <Box sx={{ width: '100%', minWidth: 0 }}>
                {pieData.length > 0 ? (
                  <Box sx={{ width: '100%', height: 480 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart layout="vertical" data={pieData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} width={72} />
                        <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(v) => [formatCurrency(v), 'Revenue']} />
                        <Bar dataKey="value" radius={[0, 4, 4, 0]} minPointSize={4}>
                          {pieData.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Box sx={{ height: 480, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2">No product revenue in this range</Typography>
                  </Box>
                )}
              </Box>
              <Box sx={{ width: '100%', minWidth: 0, maxHeight: 440, overflow: 'auto' }}>
                <Table size="small" stickyHeader sx={{ width: '100%', tableLayout: 'auto' }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Revenue</TableCell>
                      <TableCell align="right">Orders</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {productSplit.length > 0
                      ? productSplit
                          .slice(productPage * ROWS_PER_PAGE, productPage * ROWS_PER_PAGE + ROWS_PER_PAGE)
                          .map((r) => (
                            <TableRow key={r.product_id}>
                              <TableCell>{(r.product_name || `Product ${r.product_id}`).slice(0, 60)}</TableCell>
                              <TableCell align="right">{formatCurrency(r.amount)}</TableCell>
                              <TableCell align="right">{r.orders ?? 0}</TableCell>
                            </TableRow>
                          ))
                      : (
                          <TableRow><TableCell colSpan={3} align="center" sx={{ color: 'text.secondary' }}>No data</TableCell></TableRow>
                        )}
                  </TableBody>
                </Table>
              </Box>
              {productSplit.length > ROWS_PER_PAGE && (
                <TablePagination
                  component="div"
                  count={productSplit.length}
                  rowsPerPage={ROWS_PER_PAGE}
                  rowsPerPageOptions={[ROWS_PER_PAGE]}
                  page={productPage}
                  onPageChange={(_, p) => setProductPage(p)}
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
                />
              )}
            </Box>
          </Paper>

          <Paper sx={{ p: 2, mb: 2, width: '100%', boxSizing: 'border-box' }} className="section">
            <Typography variant="h6" gutterBottom>Revenue by category</Typography>
            <Box sx={{ display: 'flex', width: '100%', gap: 2, flexDirection: 'column' }}>
              <Box sx={{ width: '100%', minWidth: 0 }}>
                {categoryPieData.length > 0 ? (
                  <Box sx={{ width: '100%', height: 480 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart layout="vertical" data={categoryPieData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} width={72} />
                        <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(v) => [formatCurrency(v), 'Revenue']} />
                        <Bar dataKey="value" radius={[0, 4, 4, 0]} minPointSize={4}>
                          {categoryPieData.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Box sx={{ height: 480, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2">No category revenue in this range</Typography>
                  </Box>
                )}
              </Box>
              <Box sx={{ width: '100%', minWidth: 0, maxHeight: 440, overflow: 'auto' }}>
                <Table size="small" stickyHeader sx={{ width: '100%', tableLayout: 'auto' }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">Revenue</TableCell>
                      <TableCell align="right">Orders</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {categorySplit.length > 0
                      ? categorySplit
                          .slice(categoryPage * ROWS_PER_PAGE, categoryPage * ROWS_PER_PAGE + ROWS_PER_PAGE)
                          .map((r) => (
                            <TableRow key={r.category_id}>
                              <TableCell>{(r.category_name || `Category ${r.category_id}`).slice(0, 60)}</TableCell>
                              <TableCell align="right">{formatCurrency(r.amount)}</TableCell>
                              <TableCell align="right">{r.orders ?? 0}</TableCell>
                            </TableRow>
                          ))
                      : (
                          <TableRow><TableCell colSpan={3} align="center" sx={{ color: 'text.secondary' }}>No data</TableCell></TableRow>
                        )}
                  </TableBody>
                </Table>
              </Box>
              {categorySplit.length > ROWS_PER_PAGE && (
                <TablePagination
                  component="div"
                  count={categorySplit.length}
                  rowsPerPage={ROWS_PER_PAGE}
                  rowsPerPageOptions={[ROWS_PER_PAGE]}
                  page={categoryPage}
                  onPageChange={(_, p) => setCategoryPage(p)}
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
                />
              )}
            </Box>
          </Paper>

          {!summary && !loading && !error && (
            <Typography color="text.secondary">Select a date range to view analytics. Data is based on completed orders.</Typography>
          )}
        </Box>
      )}
    </>
  );
}

export default function AnalyticsPage() {
  const [analyticsView, setAnalyticsView] = React.useState('trends');

  return (
    <PageLayout contentMaxWidth="xl" showHeaderImage={false} showOrgHeader>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <ToggleButtonGroup
          value={analyticsView}
          exclusive
          onChange={(_, v) => v != null && setAnalyticsView(v)}
          size="medium"
          sx={{
            alignSelf: 'flex-start',
            '& .MuiToggleButton-root': { py: 1, px: 2, fontSize: '0.95rem', fontWeight: 600 },
            '& .MuiToggleButton-root.Mui-selected': {
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': { bgcolor: 'primary.dark' },
            },
          }}
        >
          <ToggleButton value="trends">Analytics & Trends</ToggleButton>
          <ToggleButton value="compare">Compare Periods</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {analyticsView === 'trends' ? <AnalyticsTrendsView /> : <AnalyticsCompareView />}
    </PageLayout>
  );
}
